<template>
  <div>
    <FileListHeader
      :canUpload="canUpload"
      :isFileSelected="selectedFileIndex !== -1"
      :buttonsVisible="fileList.length > 0"
      :canDownloadAndPreviewFile="canDownloadAndPreviewFile"
      @addFile="addFile"
      @removeFile="removeFile"
      @previewFile="previewFile"
      @downloadFile="downloadFile"
    />
    <div v-if="!fileList.length" class="upload-picture-container">
      <FileUpload @upload="addFile" />
    </div>
    <FileList
      v-else
      :selectedIndex="selectedFileIndex"
      :files="fileList"
      @selectFile="onSelectFile"
    />
    <FilePreview
      v-if="isPreviewActive"
      :src="selectedFileUrl"
      :type="selectedItemFileType"
      @close="isPreviewActive = false"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, toRefs } from 'vue'
import { getFileBlob } from '@/services/files.service'
import FileListHeader from '@/components/general/files/FileListHeader/FileListHeader.vue'
import FileList from '@/components/general/files/FileList/FileList.vue'
import FilePreview from '@/components/general/files/FilePreview.vue'
import FileUpload from './FileUpload/FileUpload.vue'
import { FILE } from '@/interfaces/misc.interface'

interface PROPS {
  fileList: (FILE | File)[]
  canDownloadAndPreviewFile: boolean
  canUpload?: boolean
}
interface EMITS {
  (e: 'addFile', newFile: File): void
  (e: 'removeFile', index: number): void
}
const props = withDefaults(defineProps<PROPS>(), {
  canUpload: false,
  canDownloadAndPreviewFile: true
})
const { fileList, canDownloadAndPreviewFile, canUpload } = toRefs(props)
const emits = defineEmits<EMITS>()

const selectedFileIndex = ref(-1)
const selectedItemFileType = ref('')
const selectedFileUrl = ref('')
const selectedFile = ref<File | FILE | null>(null)
const isPreviewActive = ref(false)
let selectedFileName = ''

const addFile = (file: File) => {
  emits('addFile', file)
}
const removeFile = () => {
  if (selectedFileIndex.value === -1) return
  emits('removeFile', selectedFileIndex.value)
  selectedFileIndex.value = -1
  selectedFileUrl.value = ''
  selectedItemFileType.value = ''
  selectedFile.value = null
}
const downloadFile = async () => {
  if (selectedFileIndex.value === -1) return
  if (!selectedFileUrl.value.length) await setDownloadUrl()
  const fileLink = document.createElement('a')
  fileLink.href = selectedFileUrl.value
  fileLink.setAttribute('download', selectedFileName)
  document.body.appendChild(fileLink)
  fileLink.click()
}

const previewFile = async () => {
  if (!selectedFileUrl.value.length) await setDownloadUrl()
  isPreviewActive.value = true
}

const setDownloadUrl = async () => {
  try {
    const storageFileName = selectedFile?.value?.storageFileName
    if (storageFileName) {
      let endpoint
      if (storageFileName.includes('zdb-product-service')) {
        endpoint = `/${storageFileName}`
        selectedFileName = storageFileName.split('/').pop()
      } else {
        endpoint = `/documents/${storageFileName}`
        selectedFileName = selectedFile.value.name
      }
      const res = await getFileBlob(endpoint)
      const blob =
        selectedItemFileType.value === 'svg'
          ? new Blob([res.data], { type: 'image/svg+xml' })
          : new Blob([res.data])
      selectedFileUrl.value = window.URL.createObjectURL(blob)
    } else {
      selectedFileUrl.value = selectedFile?.value?.url
    }
  } catch (e) {
    console.error(e)
  }
}

const onSelectFile = async (file: File | FILE, index: number) => {
  selectedFileIndex.value = index
  if (file.storageFileName) {
    const dotIndex = file.storageFileName.lastIndexOf('.')
    selectedItemFileType.value = file.storageFileName.slice(dotIndex + 1)
    selectedFile.value = file
    URL.revokeObjectURL(selectedFileUrl.value)
    selectedFileUrl.value = ''
  } else {
    // Checks the index for the last . then slices so that we get the format of the file
    const dotIndex = file.url.lastIndexOf('.')
    selectedItemFileType.value = file.url.slice(dotIndex + 1)
    selectedFile.value = file
    URL.revokeObjectURL(selectedFileUrl.value)
    selectedFileUrl.value = ''
  }
}
</script>

<style scoped lang="scss">
.upload-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px;
}
.upload-picture-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  border: 1px solid #e5e5e5;
  box-sizing: border-box;
  border-radius: 4px;
}
</style>
